import { type FC, type ReactNode, useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@app/store/store';
import { selectBootstrapStatus, setBootstrapStatus } from '@features/authentication/store/authentication.slice';
import { bootstrapAuth } from '@features/authentication/store/authentication.thunks';
import { GenericButton } from '@shared/components/ui/custom';
import { ErrorState } from '@shared/layouts';
import { LoadingPageSpinner } from '@shared/overlays';

/**
 * Blocks app rendering until the silent auth bootstrap resolves. On first mount it fires a
 * single /refresh (the refresh token is an httpOnly cookie); while that is in flight the
 * status is 'pending' and we show a full-screen spinner. Once it settles to 'authenticated'
 * or 'anonymous', the routed app renders and the route guards take over.
 *
 * If the bootstrap couldn't reach the server (offline / timeout / 5xx) the status is 'unreachable':
 * the session may still be valid, so instead of bouncing to login we render a retry screen that
 * re-runs the bootstrap.
 */
export const AppBootstrapGate: FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectBootstrapStatus);
  const hasBootstrapped = useRef(false);

  useEffect(() => {
    if (hasBootstrapped.current) {
      return;
    }
    hasBootstrapped.current = true;
    void dispatch(bootstrapAuth());
  }, [dispatch]);

  const handleRetry = () => {
    dispatch(setBootstrapStatus('pending'));
    void dispatch(bootstrapAuth());
  };

  const handleBailout = () => {
    dispatch(setBootstrapStatus('anonymous'));
  };

  if (status === 'pending') {
    return <LoadingPageSpinner />;
  }

  if (status === 'unreachable') {
    return (
      <ErrorState
        className="min-h-screen"
        title="Cannot reach the server"
        description="We could not restore your session because the server is unreachable. Check your connection and try again."
      >
        <div className="mt-10 flex items-center justify-center gap-4">
          <GenericButton onClick={handleRetry} variant="default" label="Try again" />
          <GenericButton onClick={handleBailout} variant="outline" label="Go to Login" />
        </div>
      </ErrorState>
    );
  }

  return <>{children}</>;
};
